import { Component, Element, Host, h, Prop, Watch } from "@stencil/core";
import { debounce } from "../../../utils/debounce";

/**
 * @slot (default) - Slot for the detail items  (`<ux-iws-nb-all-in-one-detail>`)
 */
@Component({
    tag: "ux-iws-nb-all-in-one-detail-group",
    styleUrl: "nb-all-in-one-detail-group.scss",
    shadow: false,
})
export class NbAllInOneDetailGroup {
    private static uidIncrement = 0;
    private uid = `nb-all-in-one-detail-${NbAllInOneDetailGroup.uidIncrement++}`;

    /** The element reference for the component. */
    @Element() host: HTMLUxIwsNbAllInOneDetailGroupElement;

    /** Indicates if the tile is active or not */
    @Prop({ mutable: true, reflect: true }) active: boolean = false;

    /** Group identifier */
    @Prop({ reflect: true }) group: string = "";

    /** Label of the group (accessibility) */
    @Prop() label: string = "";

    @Watch("active")
    handleActiveChange(active: boolean) {
        if (!active) {
            this.host.style.height = `${this.host.scrollHeight}px`;
            this.slideOut();
            return;
        }
        const siblings =
            this.host.parentElement.querySelectorAll<HTMLUxIwsNbAllInOneDetailGroupElement>(
                "ux-iws-nb-all-in-one-detail-group"
            );
        siblings.forEach((el) => (el.active = el === this.host));
        const items = this.getDetailItems();
        const activeItem = items.find((item) => item.active) || items[0];
        if (!activeItem) {
            return;
        }
        items.forEach((el) => (el.active = el === activeItem));
        this.host.style.height = `0px`;
        this.slideIn();
    }

    private slideIn = debounce(() => {
        this.host.style.height = `${this.host.scrollHeight}px`;
        this.slideInDone();
    }, 50);

    private slideInDone = debounce(() => {
        this.host.style.height = `auto`;
    }, 300);

    private slideOut = debounce(() => {
        this.host.style.height = `0px`;
    }, 50);

    private getDetailItems = (): HTMLUxIwsNbAllInOneDetailElement[] => {
        return Array.from(
            this.host.querySelectorAll<HTMLUxIwsNbAllInOneDetailElement>(
                "ux-iws-nb-all-in-one-detail"
            )
        );
    };

    private handleClickRadio = (event: Event) => {
        const radio: HTMLUxRadioButtonElement = (
            event.target as HTMLElement
        ).closest<HTMLUxRadioButtonElement>("ux-radio-button");
        const activeIndex = parseInt(radio?.value);
        if (Number.isNaN(activeIndex)) {
            return;
        }
        this.getDetailItems().forEach((item, index) => {
            item.active = index === activeIndex;
        });
    };

    render() {
        const baseClass = "ux-iws-nb-all-in-one-detail-group";
        const items = this.getDetailItems();
        const activeItem = items.find((item) => item.active) || items[0];
        return (
            <Host
                class={{
                    [baseClass]: true,
                    [`${baseClass}--active`]: this.active,
                }}
                role="group"
                aria-label={this.label}
                aria-hidden={this.active ? "false" : "true"}
                tabindex={-1}
            >
                <div class={`${baseClass}__inner`}>
                    {items.length > 1 && (
                        <div class={`${baseClass}__navigation`}>
                            <nav class={`${baseClass}__radios`}>
                                {items.map((item, index) => (
                                    <ux-radio-button
                                        key={`${index}-${item.label}`}
                                        value={`${index}`}
                                        label={item.label}
                                        name={`all-in-one-detail-${this.group}-${this.uid}`}
                                        checked={activeItem === item}
                                        onClick={this.handleClickRadio}
                                    ></ux-radio-button>
                                ))}
                            </nav>
                        </div>
                    )}
                    <div class={`${baseClass}__items`}>
                        <slot />
                    </div>
                </div>
            </Host>
        );
    }
}
